import gql from 'graphql-tag';
import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import URL from 'url-parse';

import Text from '@redbubble/design-system/react/Text';
import Card from '@redbubble/design-system/react/Card';
import Image from '@redbubble/design-system/react/Image';
import Button from '@redbubble/design-system/react/Button';
import Box from '@redbubble/design-system/react/Box';
import { MEDIUM } from '@redbubble/design-system/react/constants';

import styles from './ArtistCard.css';
import FollowButton from '../FollowButton';
import { UniqueWithinTypeDecode } from '../../apollo/uniqueWithinType';
import messages from './messages';

import { useSignUpModal } from '../../contexts/SignUpModal';

const ArtistCard = ({
  artist,
  work,
  analyticsLabel,
  analyticsValue,
  hideFollowButton = false,
  hideShopButton = false,
  transparent,
  urlRef,
  isLoggedIn
}) => {
  let shopUrl = artist.shopUrl;

  if (shopUrl && urlRef) {
    shopUrl = new URL(shopUrl, true);
    shopUrl.query.ref = urlRef;
    shopUrl = shopUrl.toString();
  }

  const coverImage = work.image.url;

  let artistId;

  try {
    artistId = parseInt(UniqueWithinTypeDecode(artist.id), 10);
  } catch {
    artistId = artist.id;
  }

  const [openModal] = useSignUpModal();

  return (
    <Card transparent={transparent}>
      <a href={shopUrl} tabIndex="-1" className={styles.artistcoverImageLink}>
        <Image
          fluid
          src={coverImage}
          alt={artist.shopDisplayName}
          loadOnVisible
          className={styles.coverImage}
        />
      </a>
      <div className={styles.artistCardBody}>
        <a href={shopUrl} tabIndex="-1" className={styles.artistAvatarLink}>
          <Image
            src={artist.avatar}
            alt={artist.shopDisplayName}
            loadOnVisible
            className={styles.avatarImage}
          />
        </a>
        <div className={styles.artistName}>
          <a className={styles.artistNameLink} href={shopUrl} tabIndex="-1">
            <Text type="display5" className={styles.artistNameText}>
              {artist.shopDisplayName}
            </Text>
          </a>
        </div>
        <Text type="body2" muted className={styles.artistSubtitle}>
          {!!artist.publishedWorkCount && (
            <FormattedMessage
              {...messages.PublishedWorkCount}
              values={{ count: artist.publishedWorkCount }}
            />
          )}
        </Text>

        {!hideFollowButton && (
          <div className={styles.followButton}>
            <FollowButton
              openSignUpModal={openModal}
              signUpModalImage={coverImage}
              isLoggedIn={isLoggedIn}
              artistId={artistId}
              followed={artist.isFollowedArtist}
              analyticsLabel={analyticsLabel}
              analyticsValue={analyticsValue}
            />
          </div>
        )}

        {!hideShopButton && (
          <Box display="flex" justifyContent="center" marginTop={1}>
            <FormattedMessage {...messages.viewShopButtonText}>
              {text => (
                <Button href={shopUrl} size={MEDIUM} strong>
                  {text}
                </Button>
              )}
            </FormattedMessage>
          </Box>
        )}
      </div>
    </Card>
  );
};

ArtistCard.propTypes = {
  artist: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    publishedWorkCount: PropTypes.number,
    avatar: PropTypes.string,
    shopUrl: PropTypes.string,
    isFollowedArtist: PropTypes.bool,
    isLoggedIn: PropTypes.bool
  }).isRequired,
  work: PropTypes.shape({
    image: PropTypes.shape({
      url: PropTypes.string
    })
  }).isRequired,
  analyticsLabel: PropTypes.string,
  analyticsValue: PropTypes.number,
  isLoggedIn: PropTypes.bool,
  hideFollowButton: PropTypes.bool,
  hideShopButton: PropTypes.bool,
  transparent: PropTypes.bool,
  urlRef: PropTypes.string
};

ArtistCard.defaultProps = {
  transparent: false,
  urlRef: null,
  isLoggedIn: false
};

ArtistCard.fragment = gql`
  fragment ArtistCard on guru_ArtistRecommendation {
    recommendedArtist {
      id
      name
      shopDisplayName
      publishedWorkCount
      avatar
      shopUrl(locale: $locale)
      isFollowedArtist
    }

    recommendedWork {
      image(width: $workImageWidth) {
        url
      }
    }
  }
`;

ArtistCard.displayName = 'ArtistCard';

export default ArtistCard;
